import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import mapIcon from "../images/icons/map_markers/Paw_Icon_Small.png";
import "../styles/home.scss";
import pawIcon from "../images/icons/map_markers/Paw_Icon_Small.png";
import Footer from "./Footer";
import Nav from "./Nav";
import PageMeta from "./PageMeta";
import StoreCta from "./StoreCta";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import {
  Alert,
  Button,
  Skeleton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DirectionsIcon from "@mui/icons-material/Directions";
import ListIcon from "@mui/icons-material/List";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MapIcon from "@mui/icons-material/Map";
import MyLocationOutlinedIcon from "@mui/icons-material/MyLocationOutlined";
import HeroImage from "../images/Dog wash.jpg";
import staticStores from "../content/stores.json";
import {
  homeMeta,
  homeHero,
  homeEntity,
  homeKeyFacts,
  homeCrossLinks,
  locationsHeading,
} from "../content/home";
import {
  organizationSchema,
  websiteSchema,
  buildStoreListSchema,
} from "../content/seo";
import {
  FadeInUp,
  HeroEntrance,
  HeroImage as HeroImageMotion,
  HeroText,
  StaggerChildren,
  StaggerItem,
} from "./motion";

import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  InfoWindow,
} from "@react-google-maps/api";

const PostcodesIO = require("postcodesio-client");
const postcodes = new PostcodesIO("https://api.postcodes.io", {
  headers: { "User-Agent": "MyAwesomeApp/1.0.0" },
});

const DISTANCE_BATCH_SIZE = 25;
const METERS_TO_MILES = 0.000621371;
const storeListSchema = buildStoreListSchema(staticStores);

const isVisibleStore = (marker) =>
  marker.has_paw_wash && !marker.is_coming_soon;

const getDirectionsUrl = (marker) =>
  `https://www.google.com/maps/dir/?api=1&destination=${marker.latitude},${marker.longitude}`;

const formatStoreHours = (marker) =>
  marker.is_24 ? "24/7 Open" : `${marker.open_time} - ${marker.close_time}`;

function StoreCard({ marker, isSelected, onSelect }) {
  return (
    <div
      className={`singleStoreContainer${
        isSelected ? " singleStoreContainer--selected" : ""
      }`}
      onClick={() => onSelect(marker)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(marker);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="singleStoreContainer__header">
        <h3>{marker.name}</h3>
        {marker.distanceToUser != null && marker.distanceToUser !== Infinity ? (
          <span className="singleStoreContainer__distance">
            {marker.distanceToUser} mi
          </span>
        ) : null}
      </div>
      <p className="singleStoreContainer__row">
        <LocationOnIcon fontSize="small" />
        {marker.address}
      </p>
      {marker.has_paw_wash ? <p>Pawwash available</p> : null}
      <p className="singleStoreContainer__row">
        <AccessTimeIcon fontSize="small" />
        {formatStoreHours(marker)}
      </p>
      <Button
        component="a"
        href={getDirectionsUrl(marker)}
        target="_blank"
        rel="noopener noreferrer"
        variant="outlined"
        size="small"
        startIcon={<DirectionsIcon />}
        className="singleStoreContainer__directions"
        onClick={(e) => e.stopPropagation()}
      >
        Get directions
      </Button>
    </div>
  );
}

function StaticLocationList({ stores }) {
  return (
    <ul className="locations-list__items">
      {stores.map((store) => (
        <li key={store.locationPin} className="locations-list__item">
          <h3>{store.name}</h3>
          <p>
            {store.address}, {store.post_code}
          </p>
          <p>{formatStoreHours(store)}</p>
        </li>
      ))}
    </ul>
  );
}

const fetchDistancesBatched = (service, userPosition, stores) =>
  new Promise((resolve, reject) => {
    const markersWithDistance = [];
    let batchIndex = 0;

    const fetchNextBatch = () => {
      if (batchIndex >= stores.length) {
        resolve(
          markersWithDistance.sort(
            (a, b) => a.distanceToUser - b.distanceToUser
          )
        );
        return;
      }

      const batch = stores.slice(
        batchIndex,
        batchIndex + DISTANCE_BATCH_SIZE
      );
      const destinations = batch.map((marker) => ({
        lat: Number(marker.latitude),
        lng: Number(marker.longitude),
      }));

      service.getDistanceMatrix(
        {
          origins: [userPosition],
          destinations,
          travelMode: "DRIVING",
          unitSystem: 1.0,
        },
        (result, status) => {
          if (status !== "OK") {
            reject(new Error(status));
            return;
          }

          batch.forEach((marker, index) => {
            const distanceValue =
              result.rows[0]?.elements[index]?.distance?.value;
            const distanceToUser = distanceValue
              ? Number((distanceValue * METERS_TO_MILES).toFixed(2))
              : Infinity;
            markersWithDistance.push({ ...marker, distanceToUser });
          });

          batchIndex += DISTANCE_BATCH_SIZE;
          fetchNextBatch();
        }
      );
    };

    fetchNextBatch();
  });

export default function Home() {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [markerData, setMarkerData] = useState(staticStores);
  const [sortedMarkerData, setSortedMarkerData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userPostCode, setUserPostCode] = useState("");
  const [mobileView, setMobileView] = useState("list");
  const [searchError, setSearchError] = useState(null);
  const mapRef = useRef(null);

  const center = {
    lat: 55.90072430176191,
    lng: -4.230517394800327,
  };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    axios
      .get(`https://api.portal.speedqueenlaundry.scot/scotland/fromWeb`)
      .then((res) => {
        const sortedData = res.data.sort((a, b) =>
          a.locationPin.localeCompare(b.locationPin)
        );
        setMarkerData(sortedData);
      })
      .catch((err) => {
        console.log(err);
        setSearchError("Unable to load store locations. Please try again later.");
      });
  }, []);

  useEffect(() => {
    if (selectedMarker && mapRef.current) {
      mapRef.current.panTo({
        lat: Number(selectedMarker.latitude),
        lng: Number(selectedMarker.longitude),
      });
    }
  }, [selectedMarker]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  const handleStoreSelect = (marker) => {
    setSelectedMarker(marker);
    setMobileView("map");
  };

  const successCallback = async (position) => {
    setSearchError(null);
    const { latitude, longitude } = position.coords || {};
    const service = new window.google.maps.DistanceMatrixService();
    const userPosition = { lat: latitude, lng: longitude };
    const visibleStores = markerData.filter(isVisibleStore);

    try {
      const markersWithDistance = await fetchDistancesBatched(
        service,
        userPosition,
        visibleStores
      );
      setSortedMarkerData(markersWithDistance);
    } catch (err) {
      console.log(err);
      setSearchError(
        "Unable to calculate distances. Please try again in a moment."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const searchWithPostCode = async () => {
    if (userPostCode === "") {
      return;
    }

    setIsLoading(true);
    setSearchError(null);

    try {
      const result = await postcodes.lookup(userPostCode);
      if (result) {
        const { latitude, longitude } = result;
        await successCallback({
          coords: { latitude, longitude },
        });
      } else {
        setIsLoading(false);
        setSearchError(
          "Unable to find that postcode. Please check it and try again."
        );
      }
    } catch (e) {
      setIsLoading(false);
      setSearchError("Unable to fetch your postcode location.");
    }
  };

  const handleGeolocationSearch = () => {
    setIsLoading(true);
    setSearchError(null);
    navigator.geolocation.getCurrentPosition(successCallback, errorCallback);
  };

  const displayMarkers =
    sortedMarkerData.length > 0 ? sortedMarkerData : markerData;
  const visibleMarkers = displayMarkers.filter(isVisibleStore);

  const errorCallback = (error) => {
    setIsLoading(false);
    console.log("Error fallback:", error);
    setSearchError(
      "Unable to get your location. Please check your location settings and try again."
    );
  };

  return (
    <main className="home">
      <PageMeta
        meta={homeMeta}
        jsonLd={[organizationSchema, websiteSchema, storeListSchema]}
      />
      <Nav page={"home"} />
      <HeroEntrance className="hero">
        <HeroImageMotion className="hero__media">
          <img src={HeroImage} alt="Pawwash dog wash station" />
        </HeroImageMotion>
        <HeroText className="hero__text">
          <h1 className="hero__title">{homeHero.title}</h1>
          <p className="hero__subtitle">{homeHero.subtitle}</p>
          <StoreCta variant="scroll" className="hero__cta" />
        </HeroText>
      </HeroEntrance>

      <FadeInUp className="home-intro">
        <h2>{homeEntity.title}</h2>
        <p>{homeEntity.definition}</p>
        <StaggerChildren className="home-key-facts">
          {homeKeyFacts.map((fact) => (
            <StaggerItem key={fact.label} className="home-key-facts__item">
              <strong className="home-key-facts__label">{fact.label}</strong>
              <span className="home-key-facts__value">{fact.value}</span>
            </StaggerItem>
          ))}
        </StaggerChildren>
        <StaggerChildren className="home-cross-links">
          {homeCrossLinks.map((item) => (
            <StaggerItem key={item.title} className="home-cross-links__item">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <Link to={item.href}>{item.linkLabel}</Link>
            </StaggerItem>
          ))}
        </StaggerChildren>
      </FadeInUp>

      <FadeInUp id="store-locator" className="location__container">
        {isLoaded ? (
          <section
            className={`location__panel location__panel--${mobileView}`}
          >
            <div className="search_store">
              <div className="location__view-toggle">
                <ToggleButtonGroup
                  value={mobileView}
                  exclusive
                  onChange={(_, value) => {
                    if (value) {
                      setMobileView(value);
                    }
                  }}
                  aria-label="Store view"
                  size="medium"
                >
                  <ToggleButton value="list" aria-label="List view">
                    <ListIcon sx={{ mr: 0.5 }} />
                    List
                  </ToggleButton>
                  <ToggleButton value="map" aria-label="Map view">
                    <MapIcon sx={{ mr: 0.5 }} />
                    Map
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>
              <div className="locater_find_heading">
                <SearchIcon fontSize="large" />
                <Typography variant="h5" component="h2">
                  Find a store
                </Typography>
              </div>
              <div className="searchBtn">
                <div className="searchBtn__postcode">
                  <TextField
                    id="outlined-basic"
                    label="Enter your post code"
                    value={userPostCode}
                    onChange={(e) => setUserPostCode(e.target.value)}
                    variant="outlined"
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={searchWithPostCode}
                    className="searchBtn__submit"
                  >
                    Search
                  </Button>
                </div>
                <div className="findWIthLocation">
                  <Button
                    variant="contained"
                    onClick={handleGeolocationSearch}
                    endIcon={<MyLocationOutlinedIcon />}
                    className="searchBtn__near-me"
                  >
                    Find a store near me
                  </Button>
                </div>
                {searchError ? (
                  <Alert severity="error" className="searchBtn__error">
                    {searchError}
                  </Alert>
                ) : null}
              </div>
              {isLoading ? (
                <div className="search_store__loading" aria-busy="true">
                  {[1, 2, 3].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      className="search_store__skeleton"
                    />
                  ))}
                </div>
              ) : (
                <div className="eachStoreContainer">
                  <StaggerChildren>
                    {visibleMarkers.map((marker) => (
                      <StaggerItem key={marker.locationPin}>
                        <StoreCard
                          marker={marker}
                          isSelected={
                            selectedMarker?.locationPin === marker.locationPin
                          }
                          onSelect={handleStoreSelect}
                        />
                      </StaggerItem>
                    ))}
                  </StaggerChildren>
                </div>
              )}
            </div>

            <div className="mapContainer location__map">
              <GoogleMap
                mapContainerClassName="mapContainer__map"
                center={center}
                zoom={11}
                options={{ streetViewControl: false }}
                onLoad={onMapLoad}
              >
                {visibleMarkers.map((marker) => (
                  <MarkerF
                    icon={mapIcon}
                    position={{
                      lat: Number(marker.latitude),
                      lng: Number(marker.longitude),
                    }}
                    onClick={() => handleMarkerClick(marker)}
                    key={marker.locationPin}
                  />
                ))}
                {selectedMarker ? (
                  <InfoWindow
                    position={{
                      lat: Number(selectedMarker.latitude),
                      lng: Number(selectedMarker.longitude),
                    }}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <>
                      <div className="infowindow_header">
                        <img src={pawIcon} alt="pawIcon" />
                        <h3 className="infowindow_header__name">
                          {selectedMarker.name}
                        </h3>
                      </div>
                      <div className="store_info">
                        <p style={{ fontSize: "14px" }}>
                          <LocationOnIcon />
                          <b>Address: </b>
                          {`${selectedMarker.address}, ${selectedMarker.post_code}`}
                        </p>
                        <p>
                          {selectedMarker.has_paw_wash
                            ? "Pawwash available"
                            : null}
                        </p>
                        <p>
                          <AccessTimeIcon />
                          {formatStoreHours(selectedMarker)}
                        </p>
                      </div>
                    </>
                  </InfoWindow>
                ) : null}
              </GoogleMap>
            </div>
          </section>
        ) : null}
      </FadeInUp>

      <FadeInUp id="locations" className="locations-list">
        <h2>{locationsHeading}</h2>
        <p>
          Pawwash self-serve dog wash stations at Speed Queen laundry sites
          across Glasgow and Scotland.
        </p>
        <StaticLocationList stores={staticStores} />
        <noscript>
          <StaticLocationList stores={staticStores} />
        </noscript>
      </FadeInUp>

      <Footer />
    </main>
  );
}
